import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select } from '@mui/material';
import SearchInput from './SearchInput';
import GenericButton, { GenericButtonTypeEnum } from './GenericButton';

export interface MultiSelectOption {
  id: string;
  name: string;
  // Note: only for info board for grouped options - if we add more places with grouped options this should be created as generic
  kindergartenName?: string;
  locationName?: string;
}

interface GenericMultiSelectWithSearchProps {
  label: string;
  options: MultiSelectOption[];
  onClearAll: () => Promise<void>;
  onShowResults: (ids: string[]) => Promise<void>;
  searchPlaceholder?: string;
  disabled?: boolean;
  grouped?: boolean;
}

const GenericMultiSelectWithSearch: React.FC<GenericMultiSelectWithSearchProps> = ({
  label,
  options,
  onClearAll,
  onShowResults,
  searchPlaceholder,
  disabled,
  grouped
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const labelLength = label.length;
  const baseWidth = 60;
  const calcWidth = baseWidth + labelLength * 8;

  const filteredOptions = useMemo(() => {
    return options.filter(opt => opt.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const groupedOptions = useMemo(() => {
    if (!grouped) return null;

    const map: Record<string, MultiSelectOption[]> = {};
    filteredOptions.forEach(opt => {
      const key = `${opt.kindergartenName} - ${opt.locationName}`;
      if (!map[key]) map[key] = [];
      map[key].push(opt);
    });
    return map;
  }, [filteredOptions, grouped]);

  const handleShowResults = async () => {
    await onShowResults(selectedOptions);
  };

  const handleClearAll = async () => {
    setSelectedOptions([]);
    await onClearAll();
  };

  return (
    <FormControl sx={{ width: 'auto', minWidth: calcWidth }}>
      <InputLabel
        disabled={disabled}
        shrink={false}
        id={`${label}-label`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 'normal',
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 'auto'
        }}
      >
        {label}
        {selectedOptions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'var(--primary-contrast, #FFF)',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 'normal',
              height: '15px',
              minWidth: '16px',
              px: 0.5,
              borderRadius: '16px'
            }}
          >
            {selectedOptions.length}
          </Box>
        )}
      </InputLabel>
      <Select
        labelId={`${label}-label`}
        multiple
        value={selectedOptions}
        onChange={e => setSelectedOptions(e.target.value as string[])}
        disabled={disabled}
        input={<OutlinedInput notched={false} />}
        renderValue={() => ''}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '6px 16px',
          flex: '1 0 0',
          borderRadius: '10px',
          border: '1px solid var(--action-outline, #F0F0F0)',
          height: '40px',
          minWidth: selectedOptions.length > 0 ? calcWidth + 8 : calcWidth,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box'
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '10px',
              maxHeight: 300
            }
          },
          MenuListProps: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              p: 0
            }
          }
        }}
      >
        <ListSubheader
          sx={{
            py: 2,
            pb: 1,
            borderBottom: '1px solid #ccc'
          }}
          onKeyDown={e => e.stopPropagation()}
        >
          <SearchInput setSearchValue={setSearchTerm} searchTitle={searchPlaceholder || 'Search by name'} icon={false} />
        </ListSubheader>
        {grouped && groupedOptions
          ? Object.entries(groupedOptions).flatMap(([groupLabel, groupItems]) => [
              <ListSubheader key={groupLabel} sx={{ color: '#B5BCC4', fontWeight: 400 }}>
                {groupLabel}
              </ListSubheader>,
              ...groupItems.map(opt => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={!!selectedOptions.includes(opt.id)} />
                  <ListItemText primary={opt.name} />
                </MenuItem>
              ))
            ])
          : filteredOptions.map(opt => (
              <MenuItem key={opt.id} value={opt.id}>
                <Checkbox checked={!!selectedOptions.includes(opt.id)} />
                <ListItemText primary={opt.name} />
              </MenuItem>
            ))}
        <ListSubheader
          sx={{
            bgcolor: 'background.paper',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            borderTop: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '4px 8px'
          }}
        >
          <GenericButton
            sx={{ p: 2, color: '#D32F2F' }}
            buttonType={GenericButtonTypeEnum.Neutral}
            label="Clear all"
            variant="text"
            onClick={handleClearAll}
          />
          <GenericButton
            sx={{ pr: 2 }}
            buttonType={GenericButtonTypeEnum.Neutral}
            label="Show results"
            variant="text"
            onClick={handleShowResults}
          />
        </ListSubheader>
      </Select>
    </FormControl>
  );
};
export default GenericMultiSelectWithSearch;
